/**
 * VehiclesList - Listagem de veículos do cliente/contato/negócio
 *
 * Usada na aba "Veículos" do modal de cliente e reutilizável em outros
 * contextos (negócio, wizard de contrato).
 *
 * @version 1.5.0
 * RELEASE: 2026-09-26 v1.5.0 - Placa volta ao texto simples anterior, sem o
 *          leiaute Mercosul compartilhado.
 * RELEASE: 2026-09-26 v1.4.0 - Placa usa o componente Mercosul compacto
 *          compartilhado com o Kanban de Vistorias.
 * RELEASE: 2026-08-08 v1.0.0 - Criação inicial
 * RELEASE: 2026-08-08 v1.1.0 - Coluna/ação de multas em aberto (mesma regra da página de
 *          listagem: flag do tenant + permissão vehicles_fines_view)
 * RELEASE: 2026-08-09 v1.2.0 - Botão "Novo Veículo" também exige permissão de criação
 *          (canCreate('vehicles_list'), admin com bypass)
 * RELEASE: 2026-08-09 v1.3.0 - Linha clicável abre o modal 360º do veículo
 *          (VehicleDetailsModalPremium) e botão "Novo Veículo" padronizado com
 *          o botão "Nova Tarefa" (h-8 text-xs)
 */

import { useState } from 'react';
import { Car, Pencil, Plus, ReceiptText, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useCustomerVehicles,
  VEHICLE_TYPE_LABELS,
  type CustomerVehicle,
  type VehicleScope,
} from '@/hooks/useCustomerVehicles';
import { VehicleFormDialog } from './VehicleFormDialog';
import { VehicleTypeCell } from './VehicleTypeCell';

import { VehicleFinesDialog } from './VehicleFinesDialog';
// v1.3.0 — modal 360º do veículo (mesmo usado na página de listagem)
import { VehicleDetailsModalPremium } from './modal/VehicleDetailsModalPremium';
import { useVehiclesModule } from '@/hooks/useVehiclesModule';
import { usePermissionsContext } from '@/contexts/PermissionsContext';

/** Props da listagem */
interface VehiclesListProps {
  scope: VehicleScope;
  /** Permite criar/editar/excluir (default: true) */
  canManage?: boolean;
  /** Renderiza somente quando a aba estiver ativa */
  isActive?: boolean;
}

/** Formata valor em reais */
function formatCurrency(value: number | null): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

/** Lista de veículos com ações de manutenção */
export function VehiclesList({ scope, canManage = true, isActive = true }: VehiclesListProps) {
  const { vehicles, isLoading, deleteVehicle } = useCustomerVehicles(scope, isActive);
  const { finesEnabled } = useVehiclesModule();
  const { canView, canCreate, isAdmin } = usePermissionsContext();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CustomerVehicle | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Veículo cujo modal de multas está aberto
  const [finesVehicle, setFinesVehicle] = useState<CustomerVehicle | null>(null);
  // v1.3.0 — veículo cujo modal de detalhes (360º) está aberto
  const [detailsVehicleId, setDetailsVehicleId] = useState<string | null>(null);

  // Multas: exige módulo habilitado no tenant + permissão (admin tem bypass)
  const canViewFines = finesEnabled && (isAdmin || canView('vehicles_fines_view'));

  // v1.2.0: cadastro exige permissão de criação no menu de veículos (admin bypass)
  const canCreateVehicle = canManage && (isAdmin || canCreate('vehicles_list'));


  /** Abre o modal em modo criação */
  const handleNew = () => {
    setEditing(null);
    setFormOpen(true);
  };

  /** Abre o modal em modo edição */
  const handleEdit = (vehicle: CustomerVehicle) => {
    setEditing(vehicle);
    setFormOpen(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {vehicles.length} veículo(s) cadastrado(s)
        </p>
        {canCreateVehicle && (
          <Button size="sm" onClick={handleNew} className="h-8 text-xs gap-1.5">
            <Plus className="h-3.5 w-3.5" />
            Novo Veículo
          </Button>
        )}
      </div>

      {vehicles.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed py-10 text-center">
          <Car className="h-8 w-8 text-muted-foreground/60" />
          <div>
            <p className="text-sm font-medium">Nenhum veículo cadastrado</p>
            <p className="text-xs text-muted-foreground">
              Cadastre os veículos que poderão ser usados nos contratos.
            </p>
          </div>
          {canCreateVehicle && (
            <Button size="sm" variant="outline" onClick={handleNew} className="h-8 text-xs gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              Novo Veículo
            </Button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Placa</th>
                <th className="px-3 py-2 text-left font-medium">Tipo</th>
                <th className="px-3 py-2 text-left font-medium">Marca / Modelo</th>
                <th className="px-3 py-2 text-left font-medium">Ano</th>
                <th className="px-3 py-2 text-left font-medium">Cor</th>
                <th className="px-3 py-2 text-right font-medium">Valor FIPE</th>
                {canViewFines && (
                  <th className="px-3 py-2 text-right font-medium">Multas</th>
                )}
                {(canManage || canViewFines) && <th className="w-24 px-3 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y">
              {vehicles.map((vehicle) => (
                <tr
                  key={vehicle.id}
                  className="cursor-pointer hover:bg-muted/30"
                  onClick={() => setDetailsVehicleId(vehicle.id)}
                >
                  <td className="px-3 py-2 font-mono font-medium">{vehicle.plate || '—'}</td>
                  <td className="px-3 py-2">
                    {/* v1.2.0 — ícone + nome do tipo (padrão VehicleTypeCell) */}
                    <VehicleTypeCell
                      type={vehicle.vehicle_type}
                      categoryName={vehicle.category_name}
                    />
                  </td>

                  <td className="px-3 py-2">
                    {[vehicle.brand_name, vehicle.model_name].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="px-3 py-2">{vehicle.year ?? '—'}</td>
                  <td className="px-3 py-2">{vehicle.color || '—'}</td>
                  <td className="px-3 py-2 text-right">{formatCurrency(vehicle.fipe_value)}</td>

                  {/* v1.1.0 — total de multas em aberto */}
                  {canViewFines && (
                    <td className="px-3 py-2 text-right">
                      {Number(vehicle.fines_total_open ?? 0) > 0 ? (
                        <Badge className="bg-amber-500/15 text-amber-600 hover:bg-amber-500/15">
                          {formatCurrency(Number(vehicle.fines_total_open))}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {vehicle.fines_synced_at ? 'Sem multas' : '—'}
                        </span>
                      )}
                    </td>
                  )}

                  {(canManage || canViewFines) && (
                    <td className="px-3 py-2">
                      <div
                        className="flex items-center justify-end gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {canViewFines && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            title="Multas do veículo"
                            onClick={() => setFinesVehicle(vehicle)}
                          >
                            <ReceiptText className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {canManage && (
                        <>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => handleEdit(vehicle)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-destructive"
                          onClick={() => setDeletingId(vehicle.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                        </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* v1.3.0 — modal 360º do veículo (aberto pelo clique na linha) */}
      <VehicleDetailsModalPremium
        vehicleId={detailsVehicleId}
        open={!!detailsVehicleId}
        onOpenChange={(open) => !open && setDetailsVehicleId(null)}
      />

      {/* v1.1.0 — modal de multas do veículo selecionado */}
      <VehicleFinesDialog
        open={!!finesVehicle}
        onOpenChange={(open) => !open && setFinesVehicle(null)}
        vehicleId={finesVehicle?.id ?? null}
        plate={finesVehicle?.plate ?? null}
        subtitle={[finesVehicle?.brand_name, finesVehicle?.model_name]
          .filter(Boolean)
          .join(' · ')}
        canSync={canViewFines}
      />

      <VehicleFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        scope={scope}
        vehicle={editing}
      />

      <AlertDialog open={!!deletingId} onOpenChange={(open) => !open && setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir veículo</AlertDialogTitle>
            <AlertDialogDescription>
              O veículo deixará de aparecer nas listagens e não poderá ser selecionado em novos
              contratos. Contratos já gerados mantêm os dados registrados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deletingId) deleteVehicle.mutate(deletingId);
                setDeletingId(null);
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
